<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMultiSelectLabel } from 'dashboard/composables/useMultiSelectLabel';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import { useAdmin } from 'dashboard/composables/useAdmin';
import KanbanFilterAdvanced from './KanbanFilterAdvanced.vue';
import KanbanOpportunityModal from './KanbanOpportunityModal.vue';
import KanbanSort from './KanbanSort.vue';

const props = defineProps({
  selectionMode: { type: Boolean, default: false },
  selectedCount: { type: Number, default: 0 },
  viewMode: { type: String, default: 'board' },
});

const emit = defineEmits([
  'filterChange',
  'pipelineChange',
  'open-settings',
  'open-new-pipeline',
  'toggleSelection',
  'openBulk',
  'view-mode-change',
]);

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const { avatarColor, initials } = useAvatarColor();

const PIPELINE_CATEGORY_COLORS = {
  sales: '#028574',
  post_sales: '#f59e0c',
};

function pipelineCategoryColor(category) {
  return PIPELINE_CATEGORY_COLORS[category]
    ? { color: PIPELINE_CATEGORY_COLORS[category] }
    : {};
}

// ── pipelines ──────────────────────────────────────────────────────────────
const pipelines = computed(() => store.getters['kanban/pipelines']);
const activePipeline = computed(() => store.getters['kanban/activePipeline']);

// ── labels & agents ────────────────────────────────────────────────────────
const allLabels = computed(() => store.getters['labels/getLabels'] ?? []);
const allAgents = computed(() => store.getters['agents/getAgents'] ?? []);

// ── filter state ───────────────────────────────────────────────────────────
const selectedStatuses = ref([]);
const selectedTags = ref([]);
const selectedAgentIds = ref([]);
const selectedSources = ref([]);
const selectedProducts = ref([]);
const selectedWatcherIds = ref([]);
const advancedCities = ref([]);
const advancedValueMin = ref('');
const advancedValueMax = ref('');
const advancedClosingDateFrom = ref('');
const advancedClosingDateTo = ref('');
const advancedDateFrom = ref('');
const advancedDateTo = ref('');
const advancedDateField = ref('created_at');
const searchQuery = ref('');

// ── sort state ─────────────────────────────────────────────────────────────
const sortBy = ref('custom');
const sortDir = ref('asc');

// ── persistência de filtros/sort por pipeline ──────────────────────────────
const currentUserId = computed(() => store.getters.getCurrentUser?.id);
function filtersStorageKey(pipelineId) {
  return `kanban_filters_pref_${pipelineId}_${currentUserId.value}`;
}

function loadPersistedFilters(pipelineId) {
  if (!pipelineId || !currentUserId.value) return null;
  try {
    const raw = localStorage.getItem(filtersStorageKey(pipelineId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistFilters(pipelineId) {
  if (!pipelineId || !currentUserId.value) return;
  const snapshot = {
    selectedStatuses: selectedStatuses.value,
    selectedTags: selectedTags.value,
    selectedAgentIds: selectedAgentIds.value,
    selectedSources: selectedSources.value,
    selectedProducts: selectedProducts.value,
    selectedWatcherIds: selectedWatcherIds.value,
    advancedCities: advancedCities.value,
    advancedValueMin: advancedValueMin.value,
    advancedValueMax: advancedValueMax.value,
    advancedClosingDateFrom: advancedClosingDateFrom.value,
    advancedClosingDateTo: advancedClosingDateTo.value,
    advancedDateFrom: advancedDateFrom.value,
    advancedDateTo: advancedDateTo.value,
    advancedDateField: advancedDateField.value,
    searchQuery: searchQuery.value,
    sortBy: sortBy.value,
    sortDir: sortDir.value,
  };
  try {
    localStorage.setItem(
      filtersStorageKey(pipelineId),
      JSON.stringify(snapshot)
    );
  } catch {
    // ignora falha de persistência
  }
}

function applyPersistedFilters(saved) {
  selectedStatuses.value = saved.selectedStatuses ?? [];
  selectedTags.value = saved.selectedTags ?? [];
  selectedAgentIds.value = saved.selectedAgentIds ?? [];
  selectedSources.value = saved.selectedSources ?? [];
  selectedProducts.value = saved.selectedProducts ?? [];
  selectedWatcherIds.value = saved.selectedWatcherIds ?? [];
  advancedCities.value = saved.advancedCities ?? [];
  advancedValueMin.value = saved.advancedValueMin ?? '';
  advancedValueMax.value = saved.advancedValueMax ?? '';
  advancedClosingDateFrom.value = saved.advancedClosingDateFrom ?? '';
  advancedClosingDateTo.value = saved.advancedClosingDateTo ?? '';
  advancedDateFrom.value = saved.advancedDateFrom ?? '';
  advancedDateTo.value = saved.advancedDateTo ?? '';
  advancedDateField.value = saved.advancedDateField ?? 'created_at';
  searchQuery.value = saved.searchQuery ?? '';
  sortBy.value = saved.sortBy ?? 'custom';
  sortDir.value = saved.sortDir ?? 'asc';
}

watch(
  activePipeline,
  pipeline => {
    if (!pipeline) return;
    applyPersistedFilters(loadPersistedFilters(pipeline.id) ?? {});
  },
  { immediate: true }
);

// ── dropdown state ─────────────────────────────────────────────────────────
const openDropdown = ref(null);
const dropdownAlign = ref('left'); // 'left' | 'right' — recalculado a cada abertura
const dropdownPos = ref({ top: 0, left: 0 });

function calcFlip(triggerEl, dropdownWidthPx) {
  if (!triggerEl) return 'left';
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  return rect.left + dropdownWidthPx > window.innerWidth - margin
    ? 'right'
    : 'left';
}

function calcPos(triggerEl, dropdownWidthPx, align) {
  if (!triggerEl) return { top: 0, left: 0 };
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  const top = rect.bottom + 4;
  const rawLeft = align === 'right' ? rect.right - dropdownWidthPx : rect.left;
  const maxLeft = window.innerWidth - dropdownWidthPx - margin;
  const left = Math.max(margin, Math.min(rawLeft, maxLeft));
  return { top, left };
}
const pipelineSearch = ref('');
const pipelineHighlight = ref(0);
const tagSearch = ref('');
const tagHighlight = ref(0);
const agentSearch = ref('');
const agentHighlight = ref(0);
const statusSearch = ref('');
const statusHighlight = ref(0);

// ── advanced filters ───────────────────────────────────────────────────────
const advancedFilters = ref({});

// ── labels computados ──────────────────────────────────────────────────────
const isPostSales = computed(
  () => activePipeline.value?.category === 'post_sales'
);
const ALL_STATUSES = computed(() =>
  isPostSales.value
    ? ['open', 'waiting', 'won', 'lost']
    : ['open', 'won', 'lost']
);
const isAllStatuses = computed(
  () =>
    selectedStatuses.value.length === 0 ||
    ALL_STATUSES.value.every(s => selectedStatuses.value.includes(s))
);
const statusCount = computed(() =>
  isAllStatuses.value ? 0 : selectedStatuses.value.length
);
const agentCount = computed(() => selectedAgentIds.value.length);
const dateActive = computed(
  () => !!(advancedDateFrom.value || advancedDateTo.value)
);
const closingDateActive = computed(
  () => !!(advancedClosingDateFrom.value || advancedClosingDateTo.value)
);

const advancedFilterCount = computed(
  () =>
    Object.values(advancedFilters.value).reduce(
      (sum, group) => sum + Object.keys(group ?? {}).length,
      0
    ) +
    selectedTags.value.length +
    selectedSources.value.length +
    selectedProducts.value.length +
    selectedWatcherIds.value.length +
    advancedCities.value.length +
    (advancedValueMin.value || advancedValueMax.value ? 1 : 0) +
    (closingDateActive.value ? 1 : 0) +
    (dateActive.value ? 1 : 0)
);

const hasActiveFilters = computed(
  () => statusCount.value > 0 || agentCount.value > 0 || dateActive.value
);

const STATUS_OPTIONS = computed(() =>
  isPostSales.value
    ? [
        { key: 'open', label: t('KANBAN.FILTERS.STATUS_OPEN_POST_SALES') },
        { key: 'waiting', label: t('KANBAN.FILTERS.STATUS_WAITING') },
        { key: 'won', label: t('KANBAN.FILTERS.STATUS_WON_POST_SALES') },
        { key: 'lost', label: t('KANBAN.FILTERS.STATUS_LOST_POST_SALES') },
      ]
    : [
        { key: 'open', label: t('KANBAN.FILTERS.STATUS_OPEN') },
        { key: 'won', label: t('KANBAN.FILTERS.STATUS_WON') },
        { key: 'lost', label: t('KANBAN.FILTERS.STATUS_LOST') },
      ]
);

const statusTriggerEl = ref(null);
const agentTriggerEl = ref(null);
const pipelineTriggerEl = ref(null);
const advancedTriggerEl = ref(null);

const statusSelectedNames = computed(() => {
  const opts = STATUS_OPTIONS.value;
  return selectedStatuses.value.map(
    key => opts.find(o => o.key === key)?.label ?? '…'
  );
});
const statusFitLabel = useMultiSelectLabel(
  statusTriggerEl,
  statusSelectedNames
);

const statusLabel = computed(
  () => statusFitLabel.value || t('KANBAN.FILTERS.STATUS')
);

const agentSelectedNames = computed(() => {
  const sel = selectedAgentIds.value;
  return sel.map(id =>
    id === 'unassigned'
      ? t('KANBAN.FILTERS.UNASSIGNED')
      : (allAgents.value.find(a => a.id === id)?.name ?? '…')
  );
});
const agentFitLabel = useMultiSelectLabel(agentTriggerEl, agentSelectedNames);

const agentLabel = computed(
  () => agentFitLabel.value || t('KANBAN.FILTERS.ASSIGNEE')
);

const filteredLabels = computed(() => {
  const q = tagSearch.value.toLowerCase();
  if (!q) return allLabels.value;
  return allLabels.value
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase();
  if (!q) return allAgents.value;
  return allAgents.value
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const ALL_AGENT_LABEL = 'todos';

const agentNavigable = computed(() => {
  if (!agentSearch.value) {
    return [
      { id: '' },
      ...allAgents.value.map(a => ({ id: a.id })),
      { id: 'unassigned' },
    ];
  }
  const items = filteredAgents.value.map(a => ({ id: a.id }));
  const showAll = ALL_AGENT_LABEL.includes(agentSearch.value.toLowerCase());
  const showUnassigned = t('KANBAN.FILTERS.UNASSIGNED')
    .toLowerCase()
    .includes(agentSearch.value.toLowerCase());
  return [
    ...(showAll ? [{ id: '' }] : []),
    ...items,
    ...(showUnassigned ? [{ id: 'unassigned' }] : []),
  ];
});

watch(agentNavigable, () => {
  agentHighlight.value = 0;
});
watch(agentSearch, () => {
  agentHighlight.value = 0;
});

// ── emit helpers (declarados antes dos keydown handlers) ──────────────────
function emitFilters() {
  const filters = {};
  filters.status = isAllStatuses.value
    ? [...ALL_STATUSES.value]
    : [...selectedStatuses.value];
  if (selectedTags.value.length) filters.labels = selectedTags.value;
  if (selectedAgentIds.value.length)
    filters.assignee_ids = selectedAgentIds.value;
  if (selectedSources.value.length) filters.opp_source = selectedSources.value;
  if (selectedProducts.value.length)
    filters.opp_products = selectedProducts.value;
  if (selectedWatcherIds.value.length)
    filters.watcher_ids = selectedWatcherIds.value;
  if (advancedCities.value.length) filters.city = advancedCities.value;
  if (advancedValueMin.value) filters.value_min = advancedValueMin.value;
  if (advancedValueMax.value) filters.value_max = advancedValueMax.value;
  if (advancedClosingDateFrom.value)
    filters.closing_date_from = advancedClosingDateFrom.value;
  if (advancedClosingDateTo.value)
    filters.closing_date_to = advancedClosingDateTo.value;
  if (advancedDateFrom.value) filters.date_from = advancedDateFrom.value;
  if (advancedDateTo.value) filters.date_to = advancedDateTo.value;
  if (
    (advancedDateFrom.value || advancedDateTo.value) &&
    advancedDateField.value !== 'created_at'
  )
    filters.date_field = advancedDateField.value;
  if (searchQuery.value) filters.search = searchQuery.value;
  if (Object.keys(advancedFilters.value).length)
    filters.custom_attributes = advancedFilters.value;
  if (sortBy.value !== 'custom') {
    filters.sort_by = sortBy.value;
    filters.sort_dir = sortDir.value;
  }
  emit('filterChange', filters);
  persistFilters(activePipeline.value?.id);
}

function closeAll() {
  openDropdown.value = null;
}

// ── toggles (declarados antes dos selects) ────────────────────────────────
function toggleAgent(id) {
  const idx = selectedAgentIds.value.indexOf(id);
  if (idx === -1) selectedAgentIds.value.push(id);
  else selectedAgentIds.value.splice(idx, 1);
  emitFilters();
}

function toggleStatus(s) {
  const idx = selectedStatuses.value.indexOf(s);
  if (idx === -1) selectedStatuses.value.push(s);
  else selectedStatuses.value.splice(idx, 1);
  emitFilters();
}

function toggleTag(title) {
  const idx = selectedTags.value.indexOf(title);
  if (idx === -1) selectedTags.value.push(title);
  else selectedTags.value.splice(idx, 1);
  emitFilters();
}

// ── selects (declarados antes dos keydown handlers) ───────────────────────
function selectAgent(id) {
  if (id === '') {
    selectedAgentIds.value = [];
    closeAll();
  } else {
    toggleAgent(id);
  }
}

function selectStatus(key) {
  if (key === '') {
    selectedStatuses.value = [];
    closeAll();
  } else {
    toggleStatus(key);
  }
}

function selectTag(title) {
  if (title === '') {
    selectedTags.value = [];
    closeAll();
  } else {
    toggleTag(title);
  }
}

// ── pipeline ───────────────────────────────────────────────────────────────
function selectPipeline(id) {
  store.dispatch('kanban/setActivePipeline', id);
  closeAll();
  emit('pipelineChange', id);
}

// ── pipeline search / keyboard ────────────────────────────────────────────
const filteredPipelines = computed(() => {
  const q = pipelineSearch.value.toLowerCase();
  if (!q) return pipelines.value;
  return pipelines.value
    .filter(p => p.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const pipelineNavigable = computed(() =>
  filteredPipelines.value.map(p => ({ id: p.id }))
);

watch(pipelineNavigable, () => {
  pipelineHighlight.value = 0;
});
watch(pipelineSearch, () => {
  pipelineHighlight.value = 0;
});

function pipelineKeydownHandler(e) {
  if (openDropdown.value !== 'pipeline') return;
  if (e.key === 'Escape') {
    openDropdown.value = null;
    pipelineSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    pipelineHighlight.value = Math.min(
      pipelineHighlight.value + 1,
      pipelineNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    pipelineHighlight.value = Math.max(pipelineHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = pipelineNavigable.value[pipelineHighlight.value];
    if (item) selectPipeline(item.id);
  } else if (e.key === 'Backspace') {
    pipelineSearch.value = pipelineSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    pipelineSearch.value += e.key;
  }
}

function isPipelineHighlighted(id) {
  return (
    pipelineNavigable.value.findIndex(i => i.id === id) ===
    pipelineHighlight.value
  );
}

function agentKeydownHandler(e) {
  if (openDropdown.value !== 'agent') return;
  if (e.key === 'Escape') {
    openDropdown.value = null;
    agentSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    agentHighlight.value = Math.min(
      agentHighlight.value + 1,
      agentNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    agentHighlight.value = Math.max(agentHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = agentNavigable.value[agentHighlight.value];
    if (item) selectAgent(item.id);
  } else if (e.key === 'Backspace') {
    agentSearch.value = agentSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    agentSearch.value += e.key;
  }
}

// ── status search / keyboard ───────────────────────────────────────────────
const ALL_STATUS_LABEL = 'todos';

const filteredStatuses = computed(() => {
  const q = statusSearch.value.toLowerCase();
  if (!q) return STATUS_OPTIONS.value;
  return STATUS_OPTIONS.value
    .filter(o => o.label.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
    );
});

const statusNavigable = computed(() => {
  if (!statusSearch.value)
    return [{ id: '' }, ...STATUS_OPTIONS.value.map(o => ({ id: o.key }))];
  const items = filteredStatuses.value.map(o => ({ id: o.key }));
  return ALL_STATUS_LABEL.includes(statusSearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

watch(statusNavigable, () => {
  statusHighlight.value = 0;
});
watch(statusSearch, () => {
  statusHighlight.value = 0;
});

function statusKeydownHandler(e) {
  if (openDropdown.value !== 'status') return;
  if (e.key === 'Escape') {
    openDropdown.value = null;
    statusSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    statusHighlight.value = Math.min(
      statusHighlight.value + 1,
      statusNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    statusHighlight.value = Math.max(statusHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = statusNavigable.value[statusHighlight.value];
    if (item) selectStatus(item.id);
  } else if (e.key === 'Backspace') {
    statusSearch.value = statusSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    statusSearch.value += e.key;
  }
}

function isStatusHighlighted(id) {
  return (
    statusNavigable.value.findIndex(i => i.id === id) === statusHighlight.value
  );
}

// ── tag search / keyboard ──────────────────────────────────────────────────
const ALL_TAG_LABEL = 'todos';

const tagNavigable = computed(() => {
  if (!tagSearch.value)
    return [{ id: '' }, ...allLabels.value.map(l => ({ id: l.title }))];
  const items = filteredLabels.value.map(l => ({ id: l.title }));
  return ALL_TAG_LABEL.includes(tagSearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

watch(tagNavigable, () => {
  tagHighlight.value = 0;
});
watch(tagSearch, () => {
  tagHighlight.value = 0;
});

function tagKeydownHandler(e) {
  if (openDropdown.value !== 'tag') return;
  if (e.key === 'Escape') {
    openDropdown.value = null;
    tagSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    tagHighlight.value = Math.min(
      tagHighlight.value + 1,
      tagNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    tagHighlight.value = Math.max(tagHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = tagNavigable.value[tagHighlight.value];
    if (item) selectTag(item.id);
  } else if (e.key === 'Backspace') {
    tagSearch.value = tagSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    tagSearch.value += e.key;
  }
}

function isAgentHighlighted(id) {
  return (
    agentNavigable.value.findIndex(i => i.id === id) === agentHighlight.value
  );
}

watch(openDropdown, (val, old) => {
  if (old === 'pipeline') {
    window.removeEventListener('keydown', pipelineKeydownHandler);
    pipelineSearch.value = '';
  }
  if (val === 'pipeline')
    window.addEventListener('keydown', pipelineKeydownHandler);
  if (old === 'agent') {
    window.removeEventListener('keydown', agentKeydownHandler);
    agentSearch.value = '';
  }
  if (val === 'agent') window.addEventListener('keydown', agentKeydownHandler);
  if (old === 'status') {
    window.removeEventListener('keydown', statusKeydownHandler);
    statusSearch.value = '';
  }
  if (val === 'status')
    window.addEventListener('keydown', statusKeydownHandler);
  if (old === 'tag') {
    window.removeEventListener('keydown', tagKeydownHandler);
    tagSearch.value = '';
  }
  if (val === 'tag') window.addEventListener('keydown', tagKeydownHandler);
});

onUnmounted(() => {
  window.removeEventListener('keydown', pipelineKeydownHandler);
  window.removeEventListener('keydown', agentKeydownHandler);
  window.removeEventListener('keydown', statusKeydownHandler);
  window.removeEventListener('keydown', tagKeydownHandler);
});

const DROPDOWN_WIDTHS = {
  pipeline: 200, // min-w-[200px]
  agent: 176, // w-44
  status: 176, // w-44
  advanced: 288, // w-72
};
const DROPDOWN_FORCE_RIGHT = { advanced: true };

function toggleDropdown(name, triggerEl) {
  const isOpening = openDropdown.value !== name;
  openDropdown.value = isOpening ? name : null;
  if (openDropdown.value !== 'pipeline') pipelineSearch.value = '';
  if (openDropdown.value !== 'tag') tagSearch.value = '';
  if (openDropdown.value !== 'agent') agentSearch.value = '';
  if (openDropdown.value !== 'status') statusSearch.value = '';
  if (isOpening) {
    const width = DROPDOWN_WIDTHS[name] ?? 0;
    dropdownAlign.value = DROPDOWN_FORCE_RIGHT[name]
      ? 'right'
      : calcFlip(triggerEl, width);
    dropdownPos.value = calcPos(triggerEl, width, dropdownAlign.value);
  }
}

function handleClickOutside(e) {
  if (
    !e.target.closest('[data-kanban-header]') &&
    !e.target.closest('[data-kanban-dropdown]')
  )
    closeAll();
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  store.dispatch('attributes/get');
  store.dispatch('agents/get');
  store.dispatch('labels/get');
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

function resetFilters() {
  selectedStatuses.value = [];
  selectedTags.value = [];
  selectedAgentIds.value = [];
  selectedSources.value = [];
  selectedProducts.value = [];
  selectedWatcherIds.value = [];
  advancedCities.value = [];
  advancedValueMin.value = '';
  advancedValueMax.value = '';
  advancedClosingDateFrom.value = '';
  advancedClosingDateTo.value = '';
  advancedFilters.value = {};
  advancedDateFrom.value = '';
  advancedDateTo.value = '';
  advancedDateField.value = 'created_at';
  emitFilters();
}

function onAdvancedFilterChange(filters) {
  const {
    labels: tags,
    opp_source: sources,
    opp_products: products,
    watcher_ids: watcherIds,
    city,
    value_min: valueMin,
    value_max: valueMax,
    closing_date_from: closingDateFrom,
    closing_date_to: closingDateTo,
    date_from: from,
    date_to: to,
    date_field: field,
    ...rest
  } = filters;
  selectedTags.value = tags ?? [];
  selectedSources.value = sources ?? [];
  selectedProducts.value = products ?? [];
  selectedWatcherIds.value = watcherIds ?? [];
  advancedCities.value = city ?? [];
  advancedValueMin.value = valueMin ?? '';
  advancedValueMax.value = valueMax ?? '';
  advancedClosingDateFrom.value = closingDateFrom ?? '';
  advancedClosingDateTo.value = closingDateTo ?? '';
  advancedDateFrom.value = from ?? '';
  advancedDateTo.value = to ?? '';
  advancedDateField.value = field ?? 'created_at';
  advancedFilters.value = rest;
  emitFilters();
}

function onSortChange(key) {
  sortBy.value = key;
  emitFilters();
}
function onSortDirChange(dir) {
  sortDir.value = dir;
  emitFilters();
}

// ── create card modal ──────────────────────────────────────────────────────
const showCreateModal = ref(false);
const virtualCard = ref(null);

function openCreateModal() {
  const pipeline = activePipeline.value;
  if (!pipeline) return;

  const firstColumn = pipeline.columns?.[0] ?? null;

  virtualCard.value = {
    pipeline_id: pipeline.id,
    column_id: firstColumn?.id ?? null,
    columns: pipeline.columns ?? [],
    title: '',
    contact: null,
    status: 'open',
    value: null,
    custom_attributes: {},
  };

  showCreateModal.value = true;
}

function onCreateModalClose() {
  showCreateModal.value = false;
  virtualCard.value = null;
  store.dispatch('kanban/fetchCards');
}

// ── bulk selection ─────────────────────────────────────────────────────────
const bulkBtnRef = ref(null);

function onBulkClick() {
  if (props.selectionMode && props.selectedCount > 0) {
    emit('openBulk', bulkBtnRef.value);
  } else {
    emit('toggleSelection');
  }
}

defineExpose({ bulkBtnRef });
</script>

<template>
  <div class="relative flex-shrink-0 border-b border-n-weak">
    <div
      data-kanban-header
      class="flex items-center gap-2 px-4 bg-n-background h-[52px] overflow-x-auto md:overflow-visible"
    >
      <!-- ── Pipeline selector ──────────────────────────────────────────── -->
      <div class="relative flex-shrink-0">
        <button
          ref="pipelineTriggerEl"
          class="flex items-center gap-1.5 px-3 py-1.5 border border-n-weak rounded-lg text-sm font-bold text-n-slate-12 hover:bg-n-alpha-1 transition-colors focus:outline-none"
          @click.stop="toggleDropdown('pipeline', $event.currentTarget)"
        >
          <template v-if="openDropdown === 'pipeline' && pipelineSearch">
            <span class="font-normal text-n-slate-10">{{
              pipelineSearch
            }}</span>
          </template>
          <template v-else>
            <span
              v-if="activePipeline?.category === 'sales'"
              class="i-lucide-trending-up size-4"
              :style="pipelineCategoryColor('sales')"
            />
            <span
              v-else-if="activePipeline?.category === 'post_sales'"
              class="i-lucide-headset size-4"
              :style="pipelineCategoryColor('post_sales')"
            />
            {{ activePipeline?.name ?? $t('KANBAN.PIPELINE.SELECTOR_LABEL') }}
          </template>
          <span class="i-lucide-chevron-down size-3.5 text-n-slate-10" />
        </button>
      </div>

      <!-- divisor -->
      <span class="w-px h-5 bg-n-weak flex-shrink-0" />

      <!-- ── Responsável ─────────────────────────────────────────────────── -->
      <div data-kanban-dropdown class="relative flex-shrink-0">
        <button
          ref="agentTriggerEl"
          class="flex items-center gap-1 text-sm border rounded-lg px-2.5 py-1.5 transition-colors focus:outline-none"
          :class="
            agentCount
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container'
          "
          @click.stop="toggleDropdown('agent', $event.currentTarget)"
        >
          <span
            v-if="openDropdown === 'agent' && agentSearch"
            class="text-n-slate-10"
          >
            {{ agentSearch }}
          </span>
          <span v-else>{{ agentLabel }}</span>
          <span class="i-lucide-chevron-down size-3.5 ml-0.5 text-n-slate-10" />
        </button>
      </div>

      <!-- ── Status ──────────────────────────────────────────────────────── -->
      <div data-kanban-dropdown class="relative flex-shrink-0">
        <button
          ref="statusTriggerEl"
          class="flex items-center gap-1 text-sm border rounded-lg px-2.5 py-1.5 transition-colors focus:outline-none"
          :class="
            statusCount
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container'
          "
          @click.stop="toggleDropdown('status', $event.currentTarget)"
        >
          <span
            v-if="openDropdown === 'status' && statusSearch"
            class="text-n-slate-10"
          >
            {{ statusSearch }}
          </span>
          <span v-else>{{ statusLabel }}</span>
          <span class="i-lucide-chevron-down size-3.5 ml-0.5 text-n-slate-10" />
        </button>
      </div>

      <!-- ── Filtros avançados ───────────────────────────────────────────── -->
      <div class="relative flex-shrink-0">
        <button
          ref="advancedTriggerEl"
          class="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
          :class="
            advancedFilterCount || openDropdown === 'advanced'
              ? 'text-n-brand bg-n-alpha-2'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          :title="$t('KANBAN.FILTERS.ADVANCED')"
          @click.stop="toggleDropdown('advanced', $event.currentTarget)"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
        </button>
      </div>

      <!-- ── Reset filtros ───────────────────────────────────────────────── -->
      <button
        v-if="hasActiveFilters"
        class="text-sm text-n-slate-10 hover:text-n-slate-12 px-1.5 py-1.5 flex-shrink-0"
        @click.stop="resetFilters"
      >
        <span class="i-lucide-x size-3.5" />
      </button>

      <!-- spacer -->
      <div class="flex-1" />

      <!-- ── Search ──────────────────────────────────────────────────────── -->
      <div class="group relative flex items-center flex-shrink-0">
        <div
          class="flex items-center border rounded-lg overflow-hidden transition-all duration-200 group-hover:border-n-weak group-focus-within:border-n-brand"
          :class="searchQuery ? 'border-n-weak' : 'border-transparent'"
        >
          <input
            v-model="searchQuery"
            class="text-sm bg-transparent text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:outline-none focus:ring-0 transition-all duration-200 py-1.5"
            :class="
              searchQuery
                ? 'w-44 px-2'
                : 'w-0 px-0 group-hover:w-44 group-hover:px-2 group-focus-within:w-44 group-focus-within:px-2'
            "
            :placeholder="$t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
            @input="emitFilters"
            @click.stop
          />
          <span
            class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0 mr-1.5 group-hover:text-n-slate-12 group-focus-within:text-n-brand transition-colors"
          />
        </div>
      </div>

      <!-- divisor -->
      <span class="w-px h-5 bg-n-weak flex-shrink-0" />

      <!-- ── Selecionar / Bulk ─────────────────────────────────────────── -->
      <button
        ref="bulkBtnRef"
        class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm border transition-colors"
        :class="
          selectionMode
            ? 'border-n-brand text-n-brand bg-n-alpha-2'
            : 'border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1'
        "
        @click="onBulkClick"
      >
        <span class="i-lucide-list-checks size-3.5" />
        <span v-if="selectedCount > 0">{{
          $t('KANBAN.BULK_ACTIONS.HEADER', { count: selectedCount })
        }}</span>
        <span
          v-if="selectedCount > 0"
          class="i-lucide-x size-3.5 ml-0.5 opacity-70 hover:opacity-100"
          @click.stop="emit('toggleSelection')"
        />
      </button>

      <!-- divisor -->
      <span class="w-px h-5 bg-n-weak flex-shrink-0" />

      <!-- ── Sort ────────────────────────────────────────────────────────── -->
      <KanbanSort
        :model-value="sortBy"
        :direction="sortDir"
        @update:model-value="onSortChange"
        @update:direction="onSortDirChange"
      />

      <!-- divisor -->
      <span class="w-px h-5 bg-n-weak flex-shrink-0" />

      <!-- ── Toggle Kanban / Lista / Calendário ───────────────────────────── -->
      <div
        class="flex items-center gap-0.5 p-0.5 border border-n-weak rounded-lg flex-shrink-0"
      >
        <button
          class="px-2.5 py-1 text-sm rounded-md transition-colors"
          :class="
            viewMode === 'board'
              ? 'bg-n-alpha-2 text-n-brand font-medium'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('view-mode-change', 'board')"
        >
          {{ $t('KANBAN.LIST_VIEW.TOGGLE_BOARD') }}
        </button>
        <button
          class="px-2.5 py-1 text-sm rounded-md transition-colors"
          :class="
            viewMode === 'list'
              ? 'bg-n-alpha-2 text-n-brand font-medium'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('view-mode-change', 'list')"
        >
          {{ $t('KANBAN.LIST_VIEW.TOGGLE_LIST') }}
        </button>
        <button
          class="px-2.5 py-1 text-sm rounded-md transition-colors"
          :class="
            viewMode === 'calendar'
              ? 'bg-n-alpha-2 text-n-brand font-medium'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('view-mode-change', 'calendar')"
        >
          {{ $t('KANBAN.LIST_VIEW.TOGGLE_CALENDAR') }}
        </button>
      </div>

      <!-- ── Nova oportunidade ───────────────────────────────────────────── -->
      <button
        class="flex items-center justify-center w-8 h-8 bg-n-brand text-white rounded-lg transition-colors hover:brightness-110 flex-shrink-0"
        :title="$t('KANBAN.CREATE_CARD.TITLE')"
        @click.stop="openCreateModal"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.8"
          stroke-linecap="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
    <div
      class="md:hidden pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-n-background to-transparent"
    />
  </div>

  <Teleport to="body">
    <!-- ── Dropdown: Pipeline ────────────────────────────────────────── -->
    <div
      v-if="openDropdown === 'pipeline'"
      data-kanban-dropdown
      class="fixed z-50 bg-n-background border border-n-weak rounded-xl shadow-xl min-w-[200px] py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <div
        v-for="p in filteredPipelines"
        :key="p.id"
        class="group flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          isPipelineHighlighted(p.id)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : p.id === activePipeline?.id
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12'
        "
      >
        <button
          class="flex items-center gap-2 flex-1 text-left min-w-0 focus:outline-none"
          @click.stop="selectPipeline(p.id)"
        >
          <span
            v-if="p.category === 'sales'"
            class="i-lucide-trending-up size-4 flex-shrink-0"
            :style="pipelineCategoryColor('sales')"
          />
          <span
            v-else-if="p.category === 'post_sales'"
            class="i-lucide-headset size-4 flex-shrink-0"
            :style="pipelineCategoryColor('post_sales')"
          />
          <span class="truncate">{{ p.name }}</span>
        </button>
        <button
          v-if="isAdmin"
          class="opacity-0 group-hover:opacity-100 flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all flex-shrink-0 focus:outline-none"
          :title="$t('KANBAN.PIPELINE.SETTINGS')"
          @click.stop="
            selectPipeline(p.id);
            emit('open-settings');
            closeAll();
          "
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
          </svg>
        </button>
      </div>
      <p
        v-if="pipelineSearch && !filteredPipelines.length"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
      </p>
      <div v-if="isAdmin" class="border-t border-n-weak mt-1 pt-1">
        <button
          class="w-full text-left px-3 py-2 text-sm text-n-slate-12/50 hover:text-n-slate-12 hover:bg-n-alpha-1 flex items-center gap-2 font-medium focus:outline-none transition-colors"
          @click.stop="
            emit('open-new-pipeline');
            closeAll();
          "
        >
          {{ $t('KANBAN.PIPELINE.NEW') }}
        </button>
      </div>
    </div>

    <!-- ── Dropdown: Responsável ────────────────────────────────────────── -->
    <div
      v-if="openDropdown === 'agent'"
      data-kanban-dropdown
      class="fixed z-50 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="agentNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="
            isAgentHighlighted('')
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !agentCount
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
          "
          @click="selectAgent('')"
        >
          {{ $t('KANBAN.FILTERS.ALL') }}
        </button>
      </template>
      <button
        v-for="agent in filteredAgents"
        :key="agent.id"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="
          isAgentHighlighted(agent.id)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : selectedAgentIds.includes(agent.id)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12'
        "
        @click="selectAgent(agent.id)"
      >
        <div
          class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
          :style="
            agent.thumbnail
              ? {
                  backgroundImage: `url(${agent.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { backgroundColor: avatarColor(agent) }
          "
        >
          <span v-if="!agent.thumbnail">{{ initials(agent.name) }}</span>
        </div>
        <span class="truncate">{{ agent.name }}</span>
        <span
          v-if="selectedAgentIds.includes(agent.id)"
          class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
        />
      </button>
      <p
        v-if="agentSearch && !agentNavigable.length"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
      </p>
      <template v-if="agentNavigable.some(i => i.id === 'unassigned')">
        <div class="border-t border-n-weak my-1" />
        <button
          class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none whitespace-nowrap"
          :class="
            isAgentHighlighted('unassigned')
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : selectedAgentIds.includes('unassigned')
                ? 'text-n-brand font-medium'
                : 'text-n-slate-11'
          "
          @click="selectAgent('unassigned')"
        >
          <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
          {{ $t('KANBAN.FILTERS.UNASSIGNED') }}
          <span
            v-if="selectedAgentIds.includes('unassigned')"
            class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
          />
        </button>
      </template>
    </div>

    <!-- ── Dropdown: Status ─────────────────────────────────────────────── -->
    <div
      v-if="openDropdown === 'status'"
      data-kanban-dropdown
      class="fixed z-50 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="statusNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="
            isStatusHighlighted('')
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !statusCount
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
          "
          @click="selectStatus('')"
        >
          {{ $t('KANBAN.FILTERS.ALL') }}
        </button>
      </template>
      <button
        v-for="opt in filteredStatuses"
        :key="opt.key"
        class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="
          isStatusHighlighted(opt.key)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : selectedStatuses.includes(opt.key)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12'
        "
        @click="selectStatus(opt.key)"
      >
        {{ opt.label }}
        <span
          v-if="selectedStatuses.includes(opt.key)"
          class="i-lucide-check size-3 text-n-brand flex-shrink-0"
        />
      </button>
      <p
        v-if="statusSearch && !statusNavigable.length"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
      </p>
    </div>

    <!-- ── Dropdown: Filtros avançados ──────────────────────────────────── -->
    <div
      v-if="openDropdown === 'advanced'"
      data-kanban-dropdown
      class="fixed z-50"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <KanbanFilterAdvanced
        :selected-tags="selectedTags"
        :all-labels="allLabels"
        :selected-sources="selectedSources"
        :selected-products="selectedProducts"
        :selected-watcher-ids="selectedWatcherIds"
        :selected-cities="advancedCities"
        :initial-value-min="advancedValueMin"
        :initial-value-max="advancedValueMax"
        :initial-closing-date-from="advancedClosingDateFrom"
        :initial-closing-date-to="advancedClosingDateTo"
        :initial-date-from="advancedDateFrom"
        :initial-date-to="advancedDateTo"
        :initial-date-field="advancedDateField"
        @filter-change="onAdvancedFilterChange"
        @close="closeAll"
      />
    </div>
  </Teleport>

  <KanbanOpportunityModal
    v-if="showCreateModal && virtualCard"
    :card="virtualCard"
    is-new
    @close="onCreateModalClose"
    @deleted="onCreateModalClose"
  />
</template>
